import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Mail, RefreshCw } from 'lucide-react';
import EmojiPicker, { EmojiStyle, EmojiClickData, Theme } from 'emoji-picker-react';
import * as api from '../services/api';
import AvatarEditor from '../components/AvatarEditor';

const STAMPS = [
  '😊','😂','🥹','😍','🤩','👍',
  '🙏','👏','🎉','✅','❤️','🔥',
  '😅','😭','🫡','💪','🤔','😴',
  '👀','🙌','💯','⚡','🌊','⚓',
  '🚢','🐟','🌏','☀️','🌙','🐋',
];

interface User {
  id: string;
  user_name: string;
  user_role: string;
  employee_code: string;
  avatar_path?: string | null;
  vessel_id?: string | null;
  vessel_name?: string | null;
  last_message?: string | null;
  last_at?: string | null;
  last_type?: string | null;
  unread_count?: number;
  unread_urgent?: number;
  unread_announcement?: number;
}

interface Message {
  id: string;
  sender_user_id: string;
  sender_name: string;
  sender_role: string;
  content: string;
  message_type: string;
  recipient_user_id: string | null;
  created_at: string;
  is_read?: boolean;
  edited_at?: string | null;
  deleted_at?: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  system_admin: 'システム管理者',
  shore_admin:  '陸上管理者',
  captain:      '船長',
  crew:         '船員',
  admin:        '管理者',
};

const ROLE_COLOR: Record<string, string> = {
  system_admin: '#7c3aed',
  shore_admin:  '#2563eb',
  admin:        '#2563eb',
  captain:      '#d97706',
  crew:         '#475569',
};

// 種別ごとの強調度: strong=緊急(赤枠・大きめ), subtle=お知らせ(控えめ), none=通常
const TYPE_CFG: Record<string, { label: string; icon: string; emphasis: 'none' | 'subtle' | 'strong' }> = {
  announcement: { label: 'お知らせ', icon: '📢', emphasis: 'subtle' },
  urgent:       { label: '緊急',     icon: '🚨', emphasis: 'strong' },
  emergency:    { label: '緊急',     icon: '🚨', emphasis: 'strong' },
  general:      { label: '',         icon: '',   emphasis: 'none' },
};

// SQLiteのdatetime('now')はUTCだがTZマーカーなし → 'Z'を付与してUTCとして解釈
function parseUTC(iso: string): Date {
  if (!iso) return new Date(NaN);
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso.replace(' ', 'T') + 'Z';
  return new Date(s);
}

function fmtTime(iso: string) {
  try {
    const d = parseUTC(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (isToday) return `${hh}:${mm}`;
    return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
  } catch { return ''; }
}

function fmtDate(iso: string) {
  try {
    const d = parseUTC(iso);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  } catch { return ''; }
}

// 日付セパレーターを挿入するためのグループ化
function groupByDate(msgs: Message[]): Array<{ type: 'date'; date: string } | { type: 'msg'; msg: Message }> {
  const result: Array<{ type: 'date'; date: string } | { type: 'msg'; msg: Message }> = [];
  let lastDate = '';
  for (const m of msgs) {
    const d = m.created_at.slice(0, 10);
    if (d !== lastDate) {
      result.push({ type: 'date', date: m.created_at });
      lastDate = d;
    }
    result.push({ type: 'msg', msg: m });
  }
  return result;
}

type RoleFilter = 'all' | 'admin' | 'crew' | 'vessel';
const CREW_ROLES  = ['crew', 'captain'];
const ADMIN_ROLES = ['system_admin', 'shore_admin'];

// ── 一括送信・予約投稿モーダル ──────────────────────────────────────────────
const BulkSendModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [targetType, setTargetType] = useState<'all' | 'vessel' | 'position'>('all');
  const [targetValue, setTargetValue] = useState('');
  const [content, setContent] = useState('');
  const [mtype, setMtype] = useState<'general' | 'announcement' | 'urgent'>('general');
  const [scheduleMode, setScheduleMode] = useState(false);
  const [when, setWhen] = useState('');
  const [vessels, setVessels] = useState<{ id: string; vessel_name: string }[]>([]);
  const [positions, setPositions] = useState<{ id: string; name: string }[]>([]);
  const [scheduled, setScheduled] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const loadScheduled = useCallback(() => {
    api.get<any>('/messages/scheduled').then(r => setScheduled(r?.data ?? [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get<any>('/vessels').then(r => setVessels(r?.data ?? [])).catch(() => {});
    api.get<any>('/position-masters').then(r => setPositions(r?.data ?? [])).catch(() => {});
    loadScheduled();
  }, [loadScheduled]);

  const targetLabel = (): string => {
    if (targetType === 'all') return '全員';
    if (targetType === 'vessel') return vessels.find(v => v.id === targetValue)?.vessel_name ?? '船舶';
    if (targetType === 'position') return positions.find(p => p.id === targetValue)?.name ?? '配乗役職';
    return '';
  };
  // 配乗役職は名前で判定する（vessel_crew_assignments.position は役職名）
  const positionSendValue = () => targetType === 'position'
    ? (positions.find(p => p.id === targetValue)?.name ?? targetValue) : targetValue;

  const send = async () => {
    if (!content.trim()) { setMsg('本文を入力してください'); return; }
    if (targetType !== 'all' && !targetValue) { setMsg('送信先を選択してください'); return; }
    let scheduled_at: string | undefined;
    if (scheduleMode) {
      if (!when) { setMsg('予約日時を指定してください'); return; }
      const dt = new Date(when);
      if (isNaN(dt.getTime()) || dt.getTime() < Date.now()) { setMsg('未来の日時を指定してください'); return; }
      scheduled_at = dt.toISOString();
    }
    setBusy(true); setMsg('');
    try {
      const res = await api.post<any>('/messages/bulk', {
        content: content.trim(), message_type: mtype,
        target_type: targetType, target_value: targetType === 'all' ? null : positionSendValue(),
        target_label: targetLabel(), scheduled_at,
      });
      const d = res?.data;
      if (d?.scheduled) { setMsg(`✅ 予約しました（${new Date(d.scheduled_at).toLocaleString('ja-JP')}）`); loadScheduled(); }
      else { setMsg(`✅ 送信しました（${d?.recipient_count ?? 0}名）`); }
      setContent('');
    } catch { setMsg('送信に失敗しました'); }
    setBusy(false);
  };
  const cancelScheduled = async (id: string) => {
    try { await api.del(`/messages/scheduled/${id}`); loadScheduled(); } catch { /* noop */ }
  };

  const chip = (active: boolean) => ({
    fontSize: 12, fontWeight: 700, borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
    border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`, background: active ? '#eff6ff' : '#fff',
    color: active ? '#1d4ed8' : '#64748b',
  });
  const pending = scheduled.filter(s => s.status === 'pending');

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(560px,96vw)', maxHeight: '90vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>📢 一括送信 / 予約投稿</div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        {/* 送信先 */}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>送信先</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {([['all', '全員'], ['vessel', '船舶ごと'], ['position', '配乗役職']] as const).map(([t, l]) => (
            <button key={t} onClick={() => { setTargetType(t); setTargetValue(''); }} style={chip(targetType === t)}>{l}</button>
          ))}
        </div>
        {targetType === 'vessel' && (
          <select value={targetValue} onChange={e => setTargetValue(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 12, fontSize: 13 }}>
            <option value="">船舶を選択...</option>
            {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
        )}
        {targetType === 'position' && (
          <select value={targetValue} onChange={e => setTargetValue(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 12, fontSize: 13 }}>
            <option value="">配乗役職を選択...</option>
            {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}

        {/* 種別 */}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, marginTop: targetType === 'all' ? 4 : 0 }}>種別</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {([['general', '通常'], ['announcement', '📢 お知らせ'], ['urgent', '🚨 緊急']] as const).map(([t, l]) => (
            <button key={t} onClick={() => setMtype(t)} style={chip(mtype === t)}>{l}</button>
          ))}
        </div>

        {/* 本文 */}
        <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="本文を入力..." rows={4}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', marginBottom: 12 }} />

        {/* 送信タイミング */}
        <div style={{ display: 'flex', gap: 6, marginBottom: scheduleMode ? 8 : 12 }}>
          <button onClick={() => setScheduleMode(false)} style={chip(!scheduleMode)}>今すぐ送信</button>
          <button onClick={() => setScheduleMode(true)} style={chip(scheduleMode)}>⏰ 予約</button>
        </div>
        {scheduleMode && (
          <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 12, fontSize: 13, boxSizing: 'border-box' }} />
        )}

        {msg && <div style={{ fontSize: 12, color: msg.startsWith('✅') ? '#16a34a' : '#dc2626', marginBottom: 10, fontWeight: 700 }}>{msg}</div>}

        <button disabled={busy} onClick={send}
          style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: busy ? 'wait' : 'pointer', background: busy ? '#94a3b8' : 'linear-gradient(135deg,#0ea5e9,#2563eb)' }}>
          {busy ? '処理中...' : scheduleMode ? '予約する' : `${targetLabel()}へ送信`}
        </button>

        {/* 予約投稿一覧 */}
        {pending.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', marginBottom: 8 }}>⏰ 予約中（{pending.length}件）</div>
            {pending.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                    {s.message_type === 'urgent' ? '🚨 ' : s.message_type === 'announcement' ? '📢 ' : ''}{s.target_label || '全員'} ・ {new Date(s.scheduled_at).toLocaleString('ja-JP')}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.content}</div>
                </div>
                <button onClick={() => cancelScheduled(s.id)} style={{ fontSize: 11, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', flexShrink: 0 }}>取消</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MessagesPage: React.FC = () => {
  const [users,       setUsers]       = useState<User[]>([]);
  const [allUsers,    setAllUsers]    = useState<User[]>([]); // 全ユーザー（検索元）
  // ページリロード後も選択状態を維持
  const [selectedId,  setSelectedId]  = useState<string | null>(() => sessionStorage.getItem('msg_selected_id'));
  const [selected,    setSelected]    = useState<User | null>(null);
  const [messages,    setMessages]    = useState<Message[]>([]);
  const [inputText,   setInputText]   = useState('');
  const [msgType,     setMsgType]     = useState<'general' | 'announcement' | 'urgent'>('general');
  const [sending,     setSending]     = useState(false);
  const [showPanel,  setShowPanel]  = useState(false);
  const [panelTab,   setPanelTab]   = useState<'stamp' | 'emoji'>('stamp');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter,  setRoleFilter]  = useState<RoleFilter>('all');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMsgs,  setLoadingMsgs]  = useState(false);
  const [vesselFilter, setVesselFilter] = useState('');
  const [showBulk,     setShowBulk]     = useState(false); // 一括送信・予約投稿モーダル
  const [dmSchedule,   setDmSchedule]   = useState(false); // 個別メッセージの予約送信ON/OFF
  const [dmWhen,       setDmWhen]       = useState('');     // 個別予約の日時
  const [menuId,      setMenuId]      = useState<string | null>(null); // アクションメニュー表示中のメッセージID
  const [editingId,   setEditingId]   = useState<string | null>(null); // 編集中のメッセージID
  const [editText,    setEditText]    = useState('');
  const bottomRef      = useRef<HTMLDivElement>(null);
  const pollRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const shouldScrollRef = useRef(false); // ポーリング更新では自動スクロールしない

  const adminUser = JSON.parse(localStorage.getItem('admin_user') ?? '{}');
  const myId: string = adminUser?.user_id ?? adminUser?.id ?? '';

  // ── ユーザー一覧ロード ──────────────────────────────────────
  const loadUsers = useCallback(async () => {
    // ① ユーザー一覧は必ず取得（失敗したらそこで終了）
    let allU: User[] = [];
    try {
      const usersRes = await api.get<any>('/users', { limit: '500' });
      allU = (usersRes?.data ?? []).filter((u: any) => u.id !== myId && u.user_id !== myId);
    } catch {
      setLoadingUsers(false);
      return;
    }

    // ② 会話履歴は失敗してもユーザー一覧は表示する
    type ConvMeta = { last_message: string; last_at: string; last_type: string | null; unread_count: number; unread_urgent: number; unread_announcement: number };
    const convMap: Record<string, ConvMeta> = {};
    try {
      const convsRes = await api.get<any>('/messages/conversations');
      for (const c of (convsRes?.data ?? [])) {
        convMap[c.id] = { last_message: c.last_message, last_at: c.last_at, last_type: c.last_type ?? null, unread_count: c.unread_count ?? 0, unread_urgent: c.unread_urgent ?? 0, unread_announcement: c.unread_announcement ?? 0 };
      }
    } catch (err) { console.error('[MessagesPage] conversations fetch:', err); }

    const merged: User[] = allU.map((u: any) => ({
      ...u,
      last_message:  convMap[u.id]?.last_message  ?? null,
      last_at:       convMap[u.id]?.last_at        ?? null,
      last_type:     convMap[u.id]?.last_type      ?? null,
      unread_count:  convMap[u.id]?.unread_count   ?? 0,
      unread_urgent:       convMap[u.id]?.unread_urgent       ?? 0,
      unread_announcement: convMap[u.id]?.unread_announcement ?? 0,
    }));
    // システム通知（送信者が会社ユーザーに存在しない自動通知）はユーザー一覧に無いため別途追加
    const sysConv = (convMap as any)['__system__'];
    if (sysConv) merged.push({ id: '__system__', user_name: 'システム通知', user_role: 'system', employee_code: '', ...sysConv } as any);

    // 未読の緊急がある会話を最優先で上に。次にお知らせ、以降は最新メッセージ順。
    merged.sort((a, b) => {
      const ua = (a.unread_urgent ?? 0) > 0 ? 1 : 0, ub = (b.unread_urgent ?? 0) > 0 ? 1 : 0;
      if (ua !== ub) return ub - ua;
      const na = (a.unread_announcement ?? 0) > 0 ? 1 : 0, nb = (b.unread_announcement ?? 0) > 0 ? 1 : 0;
      if (na !== nb) return nb - na;
      const hasA = !!a.last_at, hasB = !!b.last_at;
      if (hasA && !hasB) return -1;
      if (!hasA && hasB)  return 1;
      if (hasA && hasB)   return b.last_at!.localeCompare(a.last_at!);
      return a.user_name.localeCompare(b.user_name);
    });

    setAllUsers(merged);
    setUsers(merged);
    setLoadingUsers(false);

    // ページリロード後に選択状態を復元
    const savedId = sessionStorage.getItem('msg_selected_id');
    if (savedId) {
      const match = merged.find(u => u.id === savedId);
      if (match) setSelected(match);
    }
  }, [myId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── 検索 + ロールフィルタ + 船舶フィルタ ────────────────────
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    let filtered = allUsers;
    if (roleFilter === 'crew')   filtered = filtered.filter(u => CREW_ROLES.includes(u.user_role));
    if (roleFilter === 'admin')  filtered = filtered.filter(u => ADMIN_ROLES.includes(u.user_role));
    if (roleFilter === 'vessel') filtered = filtered.filter(u => CREW_ROLES.includes(u.user_role));
    if (vesselFilter) filtered = filtered.filter(u => u.vessel_name === vesselFilter);
    if (q) filtered = filtered.filter(u =>
      u.user_name.toLowerCase().includes(q) || u.employee_code.toLowerCase().includes(q)
    );
    setUsers(filtered);
  }, [searchQuery, roleFilter, vesselFilter, allUsers]);

  // ── チャット履歴ロード ──────────────────────────────────────
  const loadMessages = useCallback(async (userId: string, scrollToBottom = false, silent = false) => {
    if (scrollToBottom) shouldScrollRef.current = true;
    if (!silent) setLoadingMsgs(true);
    try {
      const res = await api.get<any>('/messages', { conversation_with: userId });
      setMessages(res?.data ?? []);
      // チャットを見ている間は常に既読にする（ポーリング時も含む）
      api.post('/messages/mark-read', { partner_id: userId }).catch(() => {});
    } catch { if (!silent) setMessages([]); }
    if (!silent) setLoadingMsgs(false);
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadMessages(selected.id, true); // 会話切り替え時は最下部へ

    // 10秒ポーリング（サイレント: ローディング表示しない）
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(selected.id, false, true), 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selected, loadMessages]);

  // スクロール: 会話を開いたとき・自分が送信したときのみ最下部へ
  useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [messages]);

  // ── 送信 ────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!inputText.trim() || sending || !selected) return;
    // 予約送信（この相手宛て）
    if (dmSchedule) {
      const dt = new Date(dmWhen);
      if (!dmWhen || isNaN(dt.getTime()) || dt.getTime() < Date.now()) { alert('未来の予約日時を指定してください'); return; }
      setSending(true);
      try {
        await api.post('/messages/bulk', {
          content: inputText.trim(), message_type: msgType,
          target_type: 'user', target_value: selected.id, target_label: selected.user_name,
          scheduled_at: dt.toISOString(),
        });
        setInputText(''); setDmSchedule(false); setDmWhen('');
        alert(`⏰ ${dt.toLocaleString('ja-JP')} に予約しました`);
      } catch (e: any) {
        alert(e.message ?? '予約に失敗しました');
      } finally { setSending(false); }
      return;
    }
    setSending(true);
    try {
      await api.post('/messages', {
        content: inputText.trim(),
        message_type: msgType,
        recipient_user_id: selected.id,
      });
      setInputText('');
      await loadMessages(selected.id, true); // 自分の送信後は最下部へ
      await loadUsers();
    } catch (e: any) {
      alert(e.message ?? '送信に失敗しました');
    } finally { setSending(false); }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleStampSend = async (stamp: string) => {
    if (sending || !selected) return;
    setSending(true);
    setShowPanel(false);
    try {
      await api.post('/messages', { content: stamp, message_type: 'stamp', recipient_user_id: selected.id });
      await loadMessages(selected.id, true);
      await loadUsers();
    } catch (e: any) {
      alert(e.message ?? '送信に失敗しました');
    } finally { setSending(false); }
  };

  // ── メッセージ編集・削除（自分の送信分のみ）────────────────────
  const startEdit = (m: Message) => { setMenuId(null); setEditingId(m.id); setEditText(m.content); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (m: Message) => {
    const t = editText.trim();
    if (!t) return;
    if (t === m.content) { cancelEdit(); return; }
    try {
      await api.put(`/messages/${m.id}`, { content: t });
      cancelEdit();
      if (selected) { await loadMessages(selected.id, false, true); loadUsers(); }
    } catch (e: any) { alert(e.message ?? '編集に失敗しました'); }
  };
  const handleDelete = async (m: Message) => {
    setMenuId(null);
    if (!window.confirm('このメッセージを削除しますか？')) return;
    try {
      await api.del(`/messages/${m.id}`);
      if (selected) { await loadMessages(selected.id, false, true); loadUsers(); }
    } catch (e: any) { alert(e.message ?? '削除に失敗しました'); }
  };

  const handleSelectUser = (u: User) => {
    setSelected(u);
    setSelectedId(u.id);
    sessionStorage.setItem('msg_selected_id', u.id);
    setMessages([]);
    setInputText('');
    setDmSchedule(false); setDmWhen('');
    // 既読マーク
    api.post('/messages/mark-read', { partner_id: u.id }).catch(() => {});
    // ローカルの未読カウントをクリア
    setAllUsers(prev => prev.map(x => x.id === u.id ? { ...x, unread_count: 0 } : x));
  };

  const grouped = groupByDate(messages);

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f0f2f5', overflow: 'hidden' }}>
      {showBulk && <BulkSendModal onClose={() => { setShowBulk(false); loadUsers(); }} />}

      {/* ─── 左パネル: ユーザーリスト ─────────────────────────── */}
      <div style={{
        width: 300, minWidth: 300, background: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        {/* ヘッダー */}
        <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}><MessageCircle size={17} /> 連絡</div>
            <button onClick={() => setShowBulk(true)}
              style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#0ea5e9,#2563eb)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              📢 一括送信
            </button>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="名前・社員コードで検索..."
            style={{
              width: '100%', padding: '8px 12px', fontSize: 13,
              border: '1px solid #e2e8f0', borderRadius: 20, outline: 'none',
              background: '#f8fafc', boxSizing: 'border-box', color: '#0f172a',
            }}
          />
          {/* 船舶プルダウン */}
          {(() => {
            const vessels = Array.from(new Set(
              allUsers.filter(u => u.vessel_name).map(u => u.vessel_name!)
            )).sort((a, b) => a.localeCompare(b, 'ja'));
            if (vessels.length === 0) return null;
            return (
              <select
                value={vesselFilter}
                onChange={e => setVesselFilter(e.target.value)}
                style={{
                  width: '100%', marginTop: 8, padding: '7px 10px', fontSize: 12,
                  border: `1px solid ${vesselFilter ? '#3b82f6' : '#e2e8f0'}`,
                  borderRadius: 8, outline: 'none', background: '#f8fafc',
                  color: vesselFilter ? '#1d4ed8' : '#374151', cursor: 'pointer',
                }}
              >
                <option value="">⛴ すべての船舶</option>
                {vessels.map(v => <option key={v} value={v}>⛴ {v}</option>)}
              </select>
            );
          })()}
          {/* フィルター／グループタブ */}
          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            {([
              { key: 'all',    label: `全員 (${allUsers.length})` },
              { key: 'crew',   label: `⚓ 船員・船長 (${allUsers.filter(u => CREW_ROLES.includes(u.user_role)).length})` },
              { key: 'admin',  label: `🖥 管理者 (${allUsers.filter(u => ADMIN_ROLES.includes(u.user_role)).length})` },
              { key: 'vessel', label: '⛴ 船名ごと' },
            ] as { key: RoleFilter; label: string }[]).map(f => (
              <button key={f.key} onClick={() => setRoleFilter(f.key)} style={{
                flex: 1, padding: '5px 4px', fontSize: 10, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                border: `1.5px solid ${roleFilter === f.key ? '#3b82f6' : '#e2e8f0'}`,
                background: roleFilter === f.key ? '#eff6ff' : '#f8fafc',
                color: roleFilter === f.key ? '#1d4ed8' : '#64748b',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{f.label}</button>
            ))}
          </div>
        </div>

        {/* ユーザーリスト */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingUsers ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>読み込み中...</div>
          ) : users.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>ユーザーが見つかりません</div>
          ) : (() => {
            // 船名ごとグループ化
            type Group = { label: string; users: User[] };
            let groups: Group[];
            if (roleFilter === 'vessel') {
              const map = new Map<string, User[]>();
              for (const u of users) {
                const key = u.vessel_name ?? '船舶未配属';
                const arr = map.get(key) ?? [];
                arr.push(u);
                map.set(key, arr);
              }
              const namedKeys = [...map.keys()].filter(k => k !== '船舶未配属').sort((a, b) => a.localeCompare(b, 'ja'));
              const allKeys = map.has('船舶未配属') ? [...namedKeys, '船舶未配属'] : namedKeys;
              groups = allKeys.map(key => ({ label: key, users: map.get(key)!.sort((a, b) => a.user_name.localeCompare(b.user_name, 'ja')) }));
            } else {
              groups = [{ label: '', users }];
            }

            // 行はコンポーネントではなくインラインJSXとして返す。
            // （render内で毎回新しいコンポーネント型を作ると全行が再マウントされ、
            //   アバターが取り直されて「出たり消えたり」する。intrinsicなdiv+key で reconcile させる）
            const renderRow = (u: User) => {
              const isActive  = selected?.id === u.id;
              const hasConv   = !!u.last_at;
              const unread    = (u.unread_count ?? 0) > 0 && !isActive;
              // 開く前に緊急/お知らせを示すバッジ。未読があれば塗り、なければ最新種別を薄く表示。
              const isUrgentType = (t?: string | null) => t === 'urgent' || t === 'emergency';
              const urgentBadge = (u.unread_urgent ?? 0) > 0
                ? { text: '🚨 緊急', bg: '#dc2626', fg: '#fff', border: '#dc2626', filled: true }
                : isUrgentType(u.last_type)
                ? { text: '🚨 緊急', bg: '#fef2f2', fg: '#dc2626', border: '#fca5a5', filled: false }
                : null;
              const announceBadge = !urgentBadge && (u.unread_announcement ?? 0) > 0
                ? { text: '📢 お知らせ', bg: '#2563eb', fg: '#fff', border: '#2563eb', filled: true }
                : !urgentBadge && u.last_type === 'announcement'
                ? { text: '📢 お知らせ', bg: '#eff6ff', fg: '#2563eb', border: '#bfdbfe', filled: false }
                : null;
              const typeBadge = urgentBadge ?? announceBadge;
              return (
                <div
                  key={u.id}
                  onClick={() => handleSelectUser(u)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', cursor: 'pointer',
                    background: isActive ? '#eff6ff' : urgentBadge?.filled ? '#fef2f2' : unread ? '#f0f9ff' : 'transparent',
                    borderLeft: `3px solid ${isActive ? '#3b82f6' : urgentBadge?.filled ? '#dc2626' : unread ? '#0ea5e9' : 'transparent'}`,
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = unread ? '#f0f9ff' : 'transparent'; }}
                >
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <AvatarEditor entityId={u.id} type="user" imagePath={u.avatar_path} displayName={u.user_name} size={44} readOnly onUpdated={() => {}} />
                    {unread && (
                      <div style={{ position: 'absolute', top: -2, right: -2, background: '#ef4444', color: 'white', borderRadius: '50%', minWidth: 18, height: 18, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', padding: '0 3px' }}>
                        {(u.unread_count ?? 0) > 99 ? '99+' : u.unread_count}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ fontWeight: (hasConv || unread) ? 700 : 500, fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.user_name}
                        </span>
                        {typeBadge && (
                          <span style={{
                            flexShrink: 0, fontSize: 10, fontWeight: 800, borderRadius: 6, padding: '1px 6px',
                            background: typeBadge.bg, color: typeBadge.fg, border: `1px solid ${typeBadge.border}`,
                            boxShadow: typeBadge.filled ? '0 1px 3px rgba(220,38,38,0.35)' : 'none',
                          }}>
                            {typeBadge.text}
                          </span>
                        )}
                      </span>
                      {u.last_at && <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0, marginLeft: 4 }}>{fmtTime(u.last_at)}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: CREW_ROLES.includes(u.user_role) ? '#0369a1' : ROLE_COLOR[u.user_role] ?? '#64748b', background: CREW_ROLES.includes(u.user_role) ? '#e0f2fe' : '#f1f5f9', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                        {CREW_ROLES.includes(u.user_role) ? '⚓ ' : ''}{ROLE_LABELS[u.user_role] ?? u.user_role}
                      </span>
                      {roleFilter !== 'vessel' && u.vessel_name && (
                        <span style={{ fontSize: 10, color: '#0369a1', background: '#e0f2fe', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>{u.vessel_name}</span>
                      )}
                      <span style={{ fontSize: 12, color: unread ? '#0369a1' : '#94a3b8', fontWeight: unread ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.last_message ?? 'タップしてメッセージを送る'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            };

            return groups.map((g, gi) => (
              <React.Fragment key={g.label || gi}>
                {g.label && (
                  <div style={{ padding: '6px 16px 3px', fontSize: 11, fontWeight: 700, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTop: gi > 0 ? '1px solid #e2e8f0' : undefined }}>
                    ⛴ {g.label} <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>{g.users.length}人</span>
                  </div>
                )}
                {g.users.map(u => renderRow(u))}
              </React.Fragment>
            ));
          })()}
        </div>
      </div>

      {/* ─── 右パネル: チャットエリア ─────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          /* 未選択プレースホルダー */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            <div style={{ marginBottom: 16 }}><MessageCircle size={56} /></div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>メッセージを送る相手を選択してください</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>左のユーザーリストから選択すると会話が表示されます</div>
          </div>
        ) : (
          <>
            {/* チャットヘッダー */}
            <div style={{
              background: '#fff',
              borderBottom: '1px solid #e2e8f0',
              padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0,
            }}>
              <AvatarEditor entityId={selected.id} type="user" imagePath={selected.avatar_path} displayName={selected.user_name} size={40} readOnly onUpdated={() => {}} />

              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{selected.user_name}</div>
                <div style={{ fontSize: 12, color: ROLE_COLOR[selected.user_role] ?? '#64748b' }}>
                  {ROLE_LABELS[selected.user_role] ?? selected.user_role}　{selected.employee_code}
                </div>
              </div>
              <button
                onClick={() => { loadMessages(selected.id, true); loadUsers(); }}
                style={{ marginLeft: 'auto', padding: '7px 16px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 12, color: '#475569', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <RefreshCw size={12} /> 更新
              </button>
            </div>

            {/* メッセージ一覧 */}
            <div onClick={() => setShowPanel(false)} style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 2, background: '#eef2f7' }}>
              {loadingMsgs ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>読み込み中...</div>
              ) : grouped.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                  <div style={{ marginBottom: 10 }}><Mail size={36} /></div>
                  <div style={{ fontSize: 14 }}>まだメッセージはありません</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>最初のメッセージを送りましょう</div>
                </div>
              ) : (() => {
                return grouped.map((item, idx) => {
                  if (item.type === 'date') {
                    return (
                      <div key={`d-${idx}`} style={{ textAlign: 'center', margin: '12px 0 8px' }}>
                        <span style={{ background: 'rgba(0,0,0,0.12)', color: '#fff', fontSize: 11, padding: '3px 14px', borderRadius: 20 }}>
                          {fmtDate(item.date)}
                        </span>
                      </div>
                    );
                  }
                  const m   = item.msg;
                  // 1対1チャットなので「送信者が会話相手でなければ自分」と判定する
                  // （localStorage のユーザーID形式に依存すると判定漏れで全件左寄せになるため）
                  const mine = m.sender_user_id !== selected.id;
                  const showKidoku = mine && m.is_read;
                  const typeCfg = (TYPE_CFG[m.message_type] ?? TYPE_CFG['general'])!;
                  const deleted = !!m.deleted_at;
                  const edited  = !!m.edited_at;
                  const isEditing = editingId === m.id;
                  const canEdit = mine && !deleted && m.message_type !== 'stamp';
                  const canDelete = mine && !deleted;
                  return (
                    <div key={m.id} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8, marginBottom: 4 }}>
                      {!mine && (
                        <AvatarEditor entityId={selected.id} type="user" imagePath={selected.avatar_path} displayName={m.sender_name} size={36} readOnly onUpdated={() => {}} />
                      )}
                      <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                        {!mine && (
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, marginLeft: 4 }}>
                            {m.sender_name}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, flexDirection: mine ? 'row-reverse' : 'row' }}>
                          {deleted ? (
                          <div style={{
                            background: '#eef2f7', color: '#94a3b8', padding: '10px 14px',
                            borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                            fontSize: 13, fontStyle: 'italic', border: '1px dashed #cbd5e1',
                          }}>
                            🚫 メッセージを削除しました
                          </div>
                        ) : isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 260 }}>
                            <textarea
                              autoFocus
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(m); }
                                if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                              }}
                              rows={2}
                              style={{ border: '1.5px solid #3b82f6', borderRadius: 10, padding: '8px 12px', fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
                            />
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button onClick={cancelEdit} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b', cursor: 'pointer' }}>キャンセル</button>
                              <button onClick={() => saveEdit(m)} disabled={!editText.trim()} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, background: editText.trim() ? '#1a237e' : '#e2e8f0', color: editText.trim() ? '#fff' : '#94a3b8', cursor: editText.trim() ? 'pointer' : 'not-allowed' }}>保存</button>
                            </div>
                          </div>
                        ) : m.message_type === 'stamp' ? (
                          <div style={{ fontSize: 64, lineHeight: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))', padding: '4px 8px' }}>
                            {m.content}
                          </div>
                        ) : (
                          <div style={{
                            background: mine
                              ? (typeCfg.emphasis === 'strong' ? '#b91c1c' : '#1a237e')
                              : (typeCfg.emphasis === 'strong' ? '#fef2f2' : typeCfg.emphasis === 'subtle' ? '#f0f7ff' : '#fff'),
                            color: mine ? '#fff' : (typeCfg.emphasis === 'strong' ? '#7f1d1d' : '#0f172a'),
                            padding: typeCfg.emphasis === 'strong' ? '13px 16px' : '10px 14px',
                            borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                            fontSize: typeCfg.emphasis === 'strong' ? 16 : 14,
                            fontWeight: typeCfg.emphasis === 'strong' ? 600 : 400,
                            lineHeight: 1.6,
                            boxShadow: typeCfg.emphasis === 'strong'
                              ? '0 0 0 2px rgba(220,38,38,0.35), 0 3px 12px rgba(220,38,38,0.28)'
                              : '0 1px 4px rgba(0,0,0,0.10)',
                            wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                            border: typeCfg.emphasis === 'strong'
                              ? '2.5px solid #dc2626'
                              : (mine ? 'none' : (typeCfg.emphasis === 'subtle' ? '1px solid #bfdbfe' : '1px solid #e2e8f0')),
                          }}>
                            {/* 緊急: 目立つヘッダー */}
                            {typeCfg.emphasis === 'strong' && (
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                fontSize: 13, fontWeight: 800, marginBottom: 7,
                                color: mine ? '#fff' : '#dc2626',
                                textShadow: mine ? '0 1px 2px rgba(0,0,0,0.25)' : 'none',
                              }}>
                                {typeCfg.icon} {typeCfg.label}メッセージ
                              </div>
                            )}
                            {/* お知らせ: 控えめラベル */}
                            {typeCfg.emphasis === 'subtle' && (
                              <div style={{
                                fontSize: 10, fontWeight: 700, marginBottom: 4, letterSpacing: '0.5px',
                                color: mine ? 'rgba(255,255,255,0.75)' : '#3b82f6',
                              }}>
                                {typeCfg.icon} {typeCfg.label}
                              </div>
                            )}
                            {m.content}
                          </div>
                        )}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', gap: 1, flexShrink: 0, marginBottom: 2 }}>
                            {showKidoku && <span style={{ fontSize: 10, color: '#3b82f6', fontWeight: 700 }}>既読</span>}
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>{edited && !deleted ? '編集済み・' : ''}{fmtTime(m.created_at)}</span>
                          </div>
                          {/* 自分の送信メッセージ: 編集・削除メニュー */}
                          {(canEdit || canDelete) && !isEditing && (
                            <div style={{ position: 'relative', alignSelf: 'center', flexShrink: 0 }}>
                              <button
                                onClick={() => setMenuId(menuId === m.id ? null : m.id)}
                                title="メニュー"
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1, padding: '2px 4px', borderRadius: 6 }}
                              >⋯</button>
                              {menuId === m.id && (
                                <>
                                  <div onClick={() => setMenuId(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                                  <div style={{ position: 'absolute', top: '100%', [mine ? 'right' : 'left']: 0, zIndex: 41, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.15)', overflow: 'hidden', minWidth: 110 } as React.CSSProperties}>
                                    {canEdit && (
                                      <button onClick={() => startEdit(m)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', background: 'none', fontSize: 13, color: '#0f172a', cursor: 'pointer' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                      >✏️ 編集</button>
                                    )}
                                    {canDelete && (
                                      <button onClick={() => handleDelete(m)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', background: 'none', fontSize: 13, color: '#dc2626', cursor: 'pointer' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                      >🗑 削除</button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
              <div ref={bottomRef} />
            </div>

            {/* 入力エリア */}
            <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
              {/* スタンプ／絵文字パネル */}
              {showPanel && (
                <div onClick={e => e.stopPropagation()} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {/* タブ */}
                  <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9' }}>
                    {(['stamp', 'emoji'] as const).map(tab => (
                      <button key={tab} onClick={() => setPanelTab(tab)} style={{
                        flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 600,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: panelTab === tab ? '#1a237e' : '#94a3b8',
                        borderBottom: panelTab === tab ? '2px solid #1a237e' : '2px solid transparent',
                        transition: 'all 0.12s',
                      }}>
                        {tab === 'stamp' ? 'スタンプ' : '絵文字'}
                      </button>
                    ))}
                  </div>
                  {/* スタンプ */}
                  {panelTab === 'stamp' && (
                    <div style={{ padding: '10px 16px 8px', maxHeight: 210, overflowY: 'auto',
                      display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 2 }}>
                      {STAMPS.map(s => (
                        <button key={s} onClick={() => handleStampSend(s)} style={{
                          fontSize: 28, lineHeight: 1, background: 'none', border: 'none',
                          cursor: 'pointer', borderRadius: 8, padding: '6px 2px',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                        >{s}</button>
                      ))}
                    </div>
                  )}
                  {/* 絵文字ピッカー */}
                  {panelTab === 'emoji' && (
                    <div onClick={e => e.stopPropagation()}>
                      <EmojiPicker
                        onEmojiClick={(data: EmojiClickData) => {
                          setInputText(prev => prev + data.emoji);
                        }}
                        emojiStyle={EmojiStyle.NATIVE}
                        theme={Theme.LIGHT}
                        height={300}
                        width="100%"
                        searchPlaceholder="絵文字を検索..."
                        previewConfig={{ showPreview: false }}
                        lazyLoadEmojis
                      />
                    </div>
                  )}
                </div>
              )}
              {/* 種別チップ */}
              <div style={{ display: 'flex', gap: 4, padding: '8px 16px 0' }}>
                {(['general', 'announcement', 'urgent'] as const).map(t => {
                  const labels: Record<string, string> = { general: '一般', announcement: 'お知らせ', urgent: '緊急' };
                  const colors: Record<string, { bg: string; color: string; border: string }> = {
                    general:      { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
                    announcement: { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
                    urgent:       { bg: '#fee2e2', color: '#be123c', border: '#fecdd3' },
                  };
                  const active = msgType === t;
                  const c = colors[t] ?? colors['general']!;
                  return (
                    <button key={t} onClick={() => setMsgType(t)} style={{
                      padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer',
                      background: active ? c.bg : 'transparent',
                      color:      active ? c.color : '#94a3b8',
                      border:     `1.5px solid ${active ? c.border : 'transparent'}`,
                      transition: 'all 0.1s',
                    }}>
                      {labels[t]}
                    </button>
                  );
                })}
                {/* 予約送信トグル */}
                <button onClick={() => setDmSchedule(v => !v)} style={{
                  padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer',
                  background: dmSchedule ? '#eef2ff' : 'transparent',
                  color: dmSchedule ? '#4338ca' : '#94a3b8',
                  border: `1.5px solid ${dmSchedule ? '#c7d2fe' : 'transparent'}`,
                }}>⏰ 予約</button>
              </div>
              {/* 予約日時入力 */}
              {dmSchedule && (
                <div style={{ padding: '8px 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#4338ca', fontWeight: 700, flexShrink: 0 }}>送信日時</span>
                  <input type="datetime-local" value={dmWhen} onChange={e => setDmWhen(e.target.value)}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid #c7d2fe', fontSize: 12, color: '#0f172a', background: '#fff' }} />
                </div>
              )}
              {/* 入力行 */}
              <div style={{ padding: '8px 12px 12px', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                {/* テキスト入力 */}
                <textarea
                  rows={1}
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`${selected.user_name}さんへメッセージ...`}
                  style={{
                    flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 22,
                    padding: '9px 16px', fontSize: 14, resize: 'none', outline: 'none',
                    background: '#f8fafc', color: '#0f172a', lineHeight: 1.5,
                    maxHeight: 96, overflowY: 'auto', fontFamily: 'inherit',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; setShowPanel(false); }}
                  onBlur={e  => { e.target.style.borderColor = '#e2e8f0'; }}
                />
                {/* フェイスボタン */}
                <button onClick={() => setShowPanel(v => !v)} style={{
                  width: 36, height: 36, background: 'none', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0, padding: 0,
                }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                    stroke={showPanel ? '#1a237e' : '#b0bec5'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M8.5 14.5s1 1.5 3.5 1.5 3.5-1.5 3.5-1.5"/>
                    <circle cx="9" cy="10" r="1.1" fill={showPanel ? '#1a237e' : '#b0bec5'} stroke="none"/>
                    <circle cx="15" cy="10" r="1.1" fill={showPanel ? '#1a237e' : '#b0bec5'} stroke="none"/>
                  </svg>
                </button>
                {/* 送信ボタン */}
                <button
                  onClick={handleSend}
                  disabled={sending || !inputText.trim()}
                  style={{
                    width: 42, height: 42, borderRadius: '50%', border: 'none',
                    background: inputText.trim() ? '#1a237e' : '#e2e8f0',
                    color: inputText.trim() ? '#fff' : '#94a3b8',
                    fontSize: 18, cursor: inputText.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    transition: 'all 0.12s',
                    boxShadow: inputText.trim() ? '0 2px 8px rgba(26,35,126,0.35)' : 'none',
                  }}
                >➤</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
